import React, { useCallback, useMemo } from 'react';

import { cn } from '../lib/utils';

import { useOptionalRouteWorkspace } from '../contexts/RouteWorkspaceContext';
import EntityWrapper from './shared/EntityWrapper';
import { Tooltip } from './shared/Tooltip';

export const ChannelEntity = ({ channel, dataTarget = null, jumbo = false, suggestion = false, truncated = false, nonInteractive = false }) => {
  const unavailable = useMemo(
    function checkUnavailable() {
      return !channel?.id;
    },
    [channel?.id],
  );

  const routeWorkspace = useOptionalRouteWorkspace();
  const hasRouteWorkspace = Boolean(routeWorkspace);
  const effectiveNonInteractive = nonInteractive || !hasRouteWorkspace;
  const isMainChannel = Boolean(routeWorkspace?.mainChannelId && String(channel?.id) === String(routeWorkspace.mainChannelId));
  const workspaceTitle = isMainChannel ? routeWorkspace?.workspace?.title : null;

  const handleClick = useCallback(
    function handleClickCallback() {
      if (unavailable || suggestion) return;
      window.dispatchEvent(new CustomEvent('output:activate-channel', { detail: { channelId: channel.id } }));
    },
    [channel?.id, unavailable, suggestion],
  );

  // The top level is the group, not a channel: the main channel renders as the
  // workspace title with no "#", matching the channel unit's home row.
  const label = workspaceTitle || `# ${channel?.name || 'Unavailable'}`;

  const entity = (
    <EntityWrapper
      data-type="channel"
      data-target={dataTarget}
      jumbo={jumbo}
      suggestion={suggestion}
      truncated={truncated}
      className={cn(
        unavailable && 'cursor-default text-gray-400',
        !unavailable && effectiveNonInteractive && 'pointer-events-none',
        !unavailable && !effectiveNonInteractive && !suggestion && 'cursor-pointer',
      )}
      onClick={effectiveNonInteractive || unavailable || suggestion ? undefined : handleClick}
    >
      {/* Channel chips match the channel unit everywhere: plain text, no flexicon. */}
      <span className={cn('truncate font-semibold', jumbo && 'text-base')}>{label}</span>
    </EntityWrapper>
  );

  // Hovering a live chip shows a plain helper instead of the old profile-card popover.
  if (effectiveNonInteractive || unavailable || suggestion) return entity;

  return (
    <Tooltip label={`Go to ${label}`} className="inline-flex min-w-0 align-baseline">
      {entity}
    </Tooltip>
  );
};
